/**
 * Pure helpers that never touch Firebase — safe to import anywhere.
 */

import type { Timestamp } from "firebase/firestore";
import type { TripStatus, TripSummary } from "./types";

// ---------------------------------------------------------------------------
// Trip status derivation
// ---------------------------------------------------------------------------

/**
 * Compute a trip's status from its dates.
 * Never stored — always recomputed so it can't go stale.
 */
export function getTripStatus(
  summary: Pick<TripSummary, "startDate" | "endDate">,
  nowMs: number = Date.now(),
): TripStatus {
  const start = summary.startDate.toMillis();
  const end = summary.endDate.toMillis();
  if (nowMs < start) return "upcoming";
  if (nowMs > end) return "past";
  return "active";
}

// ---------------------------------------------------------------------------
// Date formatting
// ---------------------------------------------------------------------------

/**
 * Format a Firestore Timestamp range: "Jun 3 – 10" or "Jun 28 – Jul 4".
 */
export function formatDateRange(
  startDate: Timestamp,
  endDate: Timestamp,
  locale = "en-US",
): string {
  const start = startDate.toDate();
  const end = endDate.toDate();

  const startMonth = start.toLocaleDateString(locale, { month: "short" });
  const startDay = start.getDate();
  const endMonth = end.toLocaleDateString(locale, { month: "short" });
  const endDay = end.getDate();

  if (startMonth === endMonth) {
    return `${startMonth} ${startDay} – ${endDay}`;
  }
  return `${startMonth} ${startDay} – ${endMonth} ${endDay}`;
}

/**
 * Format a full date like "June 3, 2026".
 */
export function formatFullDate(
  ts: Timestamp | null | undefined,
  locale = "en-US",
): string {
  if (!ts) return "";
  return ts.toDate().toLocaleDateString(locale, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * "9:41" — 24h wall clock. If startTime is null returns "".
 */
export function formatTime(
  ts: Timestamp | null | undefined,
  locale = "en-US",
): string {
  if (!ts) return "";
  return ts.toDate().toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

// ---------------------------------------------------------------------------
// Destinations
// ---------------------------------------------------------------------------

/**
 * Join a trip's destinations for display. Shows up to `max` items separated
 * by a middle dot; anything beyond `max` collapses into a `+N` suffix.
 *
 * ```
 * formatDestinations(["Barcelona"])                          → "Barcelona"
 * formatDestinations(["Barcelona", "Lisbon"])                → "Barcelona · Lisbon"
 * formatDestinations(["Barcelona", "Lisbon", "Porto"])       → "Barcelona · Lisbon +1"
 * ```
 *
 * Fixes the hero showing only `destinations[0]` — a Barcelona + Lisbon trip
 * that previously read "Barcelona" now reads "Barcelona · Lisbon".
 */
export function formatDestinations(
  destinations: readonly string[] | null | undefined,
  opts: { max?: number; separator?: string } = {},
): string {
  const list = (destinations ?? []).filter((d) => d && d.trim().length > 0);
  if (list.length === 0) return "";
  const max = opts.max ?? 2;
  const sep = opts.separator ?? " · ";
  if (list.length <= max) return list.join(sep);
  const head = list.slice(0, max).join(sep);
  const rest = list.length - max;
  return `${head} +${rest}`;
}

// ---------------------------------------------------------------------------
// Currency
// ---------------------------------------------------------------------------

/**
 * "€132.40" · always tabular-safe: uses the runtime's Intl formatter.
 */
export function formatMoney(
  amount: number,
  currency = "EUR",
  locale = "en-US",
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

/**
 * Client-safe UUID for expense entries. crypto.randomUUID is available in all
 * modern browsers, Node ≥ 19, and Capacitor WebViews.
 */
export function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // Fallback: RFC4122 v4-ish (not cryptographically strong, only used if crypto unavailable)
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
